import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Badge
} from '@mui/material';

// Material UI Icons
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import BugReportIcon from '@mui/icons-material/BugReport';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SensorsIcon from '@mui/icons-material/Sensors';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import MapIcon from '@mui/icons-material/Map';
import DevicesIcon from '@mui/icons-material/Devices';
import BiotechIcon from '@mui/icons-material/Biotech';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import LayersIcon from '@mui/icons-material/Layers';
import VideocamIcon from '@mui/icons-material/Videocam';
import EmailIcon from '@mui/icons-material/Email';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

// Authentication import
import { useAuth } from '../hooks/auth/useAuth';
import authService from '../services/auth/authService';
import ManualListDialog from './common/ManualListDialog';

// Use the SVG logo from public folder
const NavLogo = "/logo.svg";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [dashboardMenuAnchor, setDashboardMenuAnchor] = useState(null);
  const [analyticsMenuAnchor, setAnalyticsMenuAnchor] = useState(null);
  const [defectsMenuAnchor, setDefectsMenuAnchor] = useState(null);
  const [reportsMenuAnchor, setReportsMenuAnchor] = useState(null);

  // Pending access requests count for managers
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);

  // Get authentication state and functions
  const { user, logout, isAuthenticated, canManageUsers, canEdit, hasDiscipline } = useAuth();

  // Fetch pending request count for managers
  useEffect(() => {
    if (user && (user.role === 'manager' || user.role === 'admin')) {
      authService.getPendingRequestCount().then(count => {
        setPendingRequestCount(count);
      });
    }
  }, [user]);

  // Navigation items with icons (Dashboard, Analytics, Defects, and Reports moved to dropdowns)
  const navItems = [
    { id: 'map-view', label: 'Map View', path: '/map-view', icon: <MapIcon /> },
    { id: 'assets', label: 'Assets', path: '/assets', icon: <InventoryIcon /> },
    { id: 'live-view', label: 'Live View', path: '/live-view', icon: <VideocamIcon />, requiredDiscipline: 'Road Pavement' },
    // Defects moved to dropdown - see defectsMenuItems below
    // Dashboard, Analytics, and Reports moved to dropdown - see below
    // { id: 'sensors', label: 'Sensors', path: '/sensors', icon: <SensorsIcon /> },
  ];

  // Dashboard submenu items
  const dashboardMenuItems = [
    { id: 'dashboard-road', label: 'Road Dashboard', path: '/dashboard/road', icon: <DashboardIcon />, requiredDiscipline: 'Road Pavement' },
    { id: 'dashboard-bridge', label: 'Bridge Dashboard', path: '/dashboard/bridge', icon: <DashboardIcon />, requiredDiscipline: 'Structural' },
    { id: 'dashboard-slope', label: 'Slope Dashboard', path: '/dashboard/slope', icon: <DashboardIcon />, requiredDiscipline: 'Geotechnical' },
    { id: 'dashboard-high-mast', label: 'High Mast Dashboard', path: '/dashboard/high-mast', icon: <DashboardIcon />, requiredDiscipline: 'Vertical Asset' },
  ];

  // Analytics submenu items
  const analyticsMenuItems = [
    { id: 'analytics-road', label: 'Road Analytics', path: '/analytics/road', icon: <BarChartIcon />, requiredDiscipline: 'Road Pavement' },
    { id: 'analytics-bridge', label: 'Bridge Analytics', path: '/analytics/bridge', icon: <BarChartIcon />, requiredDiscipline: 'Structural' },
    { id: 'analytics-slope', label: 'Slope Analytics', path: '/analytics/slope', icon: <BarChartIcon />, requiredDiscipline: 'Geotechnical' },
    { id: 'analytics-high-mast', label: 'High Mast Analytics', path: '/analytics/high-mast', icon: <BarChartIcon />, requiredDiscipline: 'Vertical Asset' },
  ];

  // Defects submenu items
  const defectsMenuItems = [
    { id: 'defects-ground', label: 'Ground-View Defects', path: '/defects/ground-view', icon: <BugReportIcon />, requiredDiscipline: 'Road Pavement' },
    { id: 'defects-aerial', label: 'Aerial-View Defects', path: '/defects/aerial-view', icon: <BugReportIcon />, requiredDiscipline: 'Road Pavement' },
  ];

  // Reports submenu items
  const reportsMenuItems = [
    { id: 'reports-road', label: 'Road Report', path: '/reports/road', icon: <AssessmentIcon />, requiredDiscipline: 'Road Pavement' },
    { id: 'reports-bridge', label: 'Bridge Report', path: '/reports/bridge', icon: <AssessmentIcon />, requiredDiscipline: 'Structural' },
    { id: 'reports-slope', label: 'Slope Report', path: '/reports/slope', icon: <AssessmentIcon />, requiredDiscipline: 'Geotechnical' },
    { id: 'reports-high-mast', label: 'High Mast Report', path: '/reports/high-mast', icon: <AssessmentIcon />, requiredDiscipline: 'Vertical Asset' },
  ];

  // Filter menu items based on discipline
  const getFilteredMenuItems = (items) => {
    if (!user || !hasDiscipline) return [];
    return items.filter(item => {
      // If no discipline is required, always show the item
      if (!item.requiredDiscipline) {
        return true;
      }
      // Otherwise, check if the user has the required discipline
      return hasDiscipline(item.requiredDiscipline);
    });
  };

  // Get navigation items based on user role
  const getNavigationItems = () => {
    const items = getFilteredMenuItems(navItems);

    // Manager-specific items
    if (user?.role === 'manager') {
      // Removed contractors from main nav - moved to dropdown
      // items.push({
      //   id: 'manager-dashboard',
      //   label: 'Manager Dashboard',
      //   path: '/manager-dashboard',
      //   icon: <DashboardIcon />
      // });
      items.push({
        id: 'work-orders',
        label: 'Action Log',
        path: '/work-orders',
        icon: <AssignmentIcon />
      });
    }

    // Contractor- and Inspector-specific items
    if (user?.role === 'contractor' || user?.role === 'inspector') {
      items.push({
        id: 'my-work-orders',
        label: 'My Action Log',
        path: '/my-work-orders',
        icon: <AssignmentIcon />,
        requiredDiscipline: 'Road Pavement'
      });
    }

    return getFilteredMenuItems(items);
  };

  // Helper functions
  const getUserInitials = () => {
    if (!user?.fullName && !user?.email) return 'U';
    const name = user.fullName || user.email;
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getUserDisplayName = () => {
    // Add fallback while user data is being refreshed
    if (!user) return 'Loading...';
    return user?.fullName || user?.email || 'User';
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'inspector': return 'info';
      case 'viewer': return 'success';
      default: return 'default';
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setUserMenuAnchor(null);
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still redirect to login even if logout request fails
      navigate('/login');
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleDashboardMenuOpen = (event) => {
    setDashboardMenuAnchor(event.currentTarget);
  };

  const handleDashboardMenuClose = () => {
    setDashboardMenuAnchor(null);
  };

  const handleAnalyticsMenuOpen = (event) => {
    setAnalyticsMenuAnchor(event.currentTarget);
  };

  const handleAnalyticsMenuClose = () => {
    setAnalyticsMenuAnchor(null);
  };

  const handleDefectsMenuOpen = (event) => {
    setDefectsMenuAnchor(event.currentTarget);
  };

  const handleDefectsMenuClose = () => {
    setDefectsMenuAnchor(null);
  };

  const handleReportsMenuOpen = (event) => {
    setReportsMenuAnchor(event.currentTarget);
  };

  const handleReportsMenuClose = () => {
    setReportsMenuAnchor(null);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const isDashboardActive = () => {
    return location.pathname.startsWith('/dashboard') || location.pathname === '/';
  };

  const isAnalyticsActive = () => {
    return location.pathname.startsWith('/analytics');
  };

  const isDefectsActive = () => {
    return location.pathname.startsWith('/defects');
  };

  const isReportsActive = () => {
    return location.pathname.startsWith('/reports');
  };

  // Debug: Log user state changes
  useEffect(() => {
    console.log('🔧 [Navbar] User state changed:', {
      isAuthenticated,
      hasUser: !!user,
      userRole: user?.role,
      userOrg: user?.organizationName,
      fullName: user?.fullName,
      email: user?.email
    });
    console.log('🔧 [Navbar] Full user object:', user);
  }, [user, isAuthenticated]);

  // Don't render navbar if user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: 'primary.main' }}>
          IRIS
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          Road Asset Management
        </Typography>
      </Box>
      <Divider />
      <List>
        {getNavigationItems().map((item) => (
          <ListItem 
            button 
            key={item.id} 
            component={Link} 
            to={item.path}
            selected={isActivePath(item.path)}
            onClick={() => isMobile && setDrawerOpen(false)}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                '& .MuiListItemIcon-root': {
                  color: 'primary.main'
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            <ListItemIcon>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        
        {/* Dashboard submenu items */}
        {getFilteredMenuItems(dashboardMenuItems).map((item) => (
          <ListItem
            button
            key={item.id}
            component={Link} 
            to={item.path}
            selected={isActivePath(item.path)}
            onClick={() => isMobile && setDrawerOpen(false)}
            sx={{
              pl: 4, // Indent submenu items
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                '& .MuiListItemIcon-root': {
                  color: 'primary.main'
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            <ListItemIcon>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        
        {/* Analytics submenu items */}
        {getFilteredMenuItems(analyticsMenuItems).map((item) => (
          <ListItem
            button
            key={item.id}
            component={Link} 
            to={item.path}
            selected={isActivePath(item.path)}
            onClick={() => isMobile && setDrawerOpen(false)}
            sx={{
              pl: 4, // Indent submenu items
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                '& .MuiListItemIcon-root': {
                  color: 'primary.main'
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            <ListItemIcon>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        
        {/* Defects submenu items */}
        {getFilteredMenuItems(defectsMenuItems).map((item) => (
          <ListItem
            button
            key={item.id}
            component={Link} 
            to={item.path}
            selected={isActivePath(item.path)}
            onClick={() => isMobile && setDrawerOpen(false)}
            sx={{
              pl: 4, // Indent submenu items
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                '& .MuiListItemIcon-root': {
                  color: 'primary.main'
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            <ListItemIcon>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        
        {/* Reports submenu items */}
        {getFilteredMenuItems(reportsMenuItems).map((item) => (
          <ListItem
            button
            key={item.id}
            component={Link} 
            to={item.path}
            selected={isActivePath(item.path)}
            onClick={() => isMobile && setDrawerOpen(false)}
            sx={{
              pl: 4, // Indent submenu items
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                '& .MuiListItemIcon-root': {
                  color: 'primary.main'
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            <ListItemIcon>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        {user?.role === 'manager' && (
          <ListItem 
            button 
            component={Link}
            to="/contractors"
            selected={isActivePath('/contractors')}
            onClick={() => isMobile && setDrawerOpen(false)}
          >
            <ListItemIcon>
              <BusinessIcon />
            </ListItemIcon>
            <ListItemText primary="Contractors" />
          </ListItem>
        )}
        {/* Manager-specific Map Layers */}
        {user?.role === 'manager' && (
          <ListItem 
            button 
            component={Link}
            to="/map-layers"
            selected={isActivePath('/map-layers')}
            onClick={() => isMobile && setDrawerOpen(false)}
          >
            <ListItemIcon>
              <LayersIcon />
            </ListItemIcon>
            <ListItemText primary="Map Layers" />
          </ListItem>
        )}
        {/* Manager-specific menu items */}
        {user?.role === 'manager' && (
          <ListItem
            button
            component={Link}
            to="/users"
            selected={isActivePath('/users')}
            onClick={() => isMobile && setDrawerOpen(false)}
          >
            <ListItemIcon>
              <Badge badgeContent={pendingRequestCount} color="warning" max={99}>
                <PeopleIcon />
              </Badge>
            </ListItemIcon>
            <ListItemText primary="Manage Users" />
          </ListItem>
        )}
        {user?.role === 'admin' && user?.organizationId === null && (
          <ListItem 
            button 
            component={Link}
            to="/organizations"
            selected={isActivePath('/organizations')}
            onClick={() => isMobile && setDrawerOpen(false)}
          >
            <ListItemIcon>
              <BusinessIcon />
            </ListItemIcon>
            <ListItemText primary="Organizations" />
          </ListItem>
        )}
        {/* Manager-only items - hidden from user and viewer roles */}
        {user?.role === 'manager' && (
          <ListItem 
            button 
            component={Link}
            to="/status"
            selected={isActivePath('/status')}
            onClick={() => isMobile && setDrawerOpen(false)}
          >
            <ListItemIcon>
              <SignalCellularAltIcon />
            </ListItemIcon>
            <ListItemText primary="API Status" />
          </ListItem>
        )}
        {user?.role === 'manager' && (
          <ListItem
            button
            component={Link}
            to="/inspection-flow"
            selected={isActivePath('/inspection-flow')}
            onClick={() => isMobile && setDrawerOpen(false)}
          >
            <ListItemIcon>
              <AccountTreeIcon />
            </ListItemIcon>
            <ListItemText primary="Inspection Flow" />
          </ListItem>
        )}
        {user?.role === 'manager' && (
          <ListItem
            button
            component={Link}
            to="/settings"
            selected={isActivePath('/settings')}
            onClick={() => isMobile && setDrawerOpen(false)}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItem>
        )}
        {user?.role === 'manager' && (
          <ListItem 
            button 
            component={Link}
            to="/devices"
            selected={isActivePath('/devices')}
            onClick={() => isMobile && setDrawerOpen(false)}
          >
            <ListItemIcon>
              <DevicesIcon />
            </ListItemIcon>
            <ListItemText primary="Device" />
          </ListItem>
        )}
        <ListItem button sx={{ mt: 1 }} onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" elevation={0} sx={{ backgroundColor: 'white', color: 'text.primary', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          {/* Logo */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <img
              src={NavLogo}
              alt="GeoAI-R2 Logo"
              style={{
                height: '40px',
                width: 'auto',
                marginRight: '12px',
                objectFit: 'contain'
              }}
            />
          </Box>
          
          {!isMobile && (
            <Box sx={{ display: 'flex' }}>
              {getNavigationItems().map((item) => (
                <Button 
                  key={item.id}
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                  color={isActivePath(item.path) ? 'primary' : 'inherit'}
                  sx={{ 
                    mx: 0.5,
                    py: 1,
                    fontWeight: isActivePath(item.path) ? 600 : 400,
                    borderBottom: isActivePath(item.path) ? '2px solid' : '2px solid transparent',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      borderBottom: '2px solid',
                      borderColor: isActivePath(item.path) ? 'primary.main' : 'action.hover'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
              
              {/* Dashboard Dropdown - only show if user has access to any dashboard */}
              {getFilteredMenuItems(dashboardMenuItems).length > 0 && (
                <>
                  <Button
                    onClick={handleDashboardMenuOpen}
                    startIcon={<DashboardIcon />}
                    endIcon={<KeyboardArrowDownIcon />}
                    color={isDashboardActive() ? 'primary' : 'inherit'}
                    sx={{
                      mx: 0.5,
                      py: 1,
                      fontWeight: isDashboardActive() ? 600 : 400,
                      borderBottom: isDashboardActive() ? '2px solid' : '2px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        borderBottom: '2px solid',
                        borderColor: isDashboardActive() ? 'primary.main' : 'action.hover'
                      }
                    }}
                  >
                    Dashboard
                  </Button>
                  <Menu
                    anchorEl={dashboardMenuAnchor}
                    open={Boolean(dashboardMenuAnchor)}
                    onClose={handleDashboardMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    {getFilteredMenuItems(dashboardMenuItems).map((item) => (
                      <MenuItem
                        key={item.id}
                        component={Link}
                        to={item.path}
                        onClick={handleDashboardMenuClose}
                        selected={isActivePath(item.path)}
                      >
                        <ListItemIcon>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.label} />
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
              
              {/* Analytics Dropdown - only show if user has access to any analytics */}
              {getFilteredMenuItems(analyticsMenuItems).length > 0 && (
                <>
                  <Button
                    onClick={handleAnalyticsMenuOpen}
                    startIcon={<BarChartIcon />}
                    endIcon={<KeyboardArrowDownIcon />}
                    color={isAnalyticsActive() ? 'primary' : 'inherit'}
                    sx={{
                      mx: 0.5,
                      py: 1,
                      fontWeight: isAnalyticsActive() ? 600 : 400,
                      borderBottom: isAnalyticsActive() ? '2px solid' : '2px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        borderBottom: '2px solid',
                        borderColor: isAnalyticsActive() ? 'primary.main' : 'action.hover'
                      }
                    }}
                  >
                    Analytics
                  </Button>
                  <Menu
                    anchorEl={analyticsMenuAnchor}
                    open={Boolean(analyticsMenuAnchor)}
                    onClose={handleAnalyticsMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    {getFilteredMenuItems(analyticsMenuItems).map((item) => (
                      <MenuItem
                        key={item.id}
                        component={Link}
                        to={item.path}
                        onClick={handleAnalyticsMenuClose}
                        selected={isActivePath(item.path)}
                      >
                        <ListItemIcon>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.label} />
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
              
              {/* Defects Dropdown - only show if user has access to any defects view */}
              {getFilteredMenuItems(defectsMenuItems).length > 0 && (
                <>
                  <Button
                    onClick={handleDefectsMenuOpen}
                    startIcon={<BugReportIcon />}
                    endIcon={<KeyboardArrowDownIcon />}
                    color={isDefectsActive() ? 'primary' : 'inherit'}
                    sx={{
                      mx: 0.5,
                      py: 1,
                      fontWeight: isDefectsActive() ? 600 : 400,
                      borderBottom: isDefectsActive() ? '2px solid' : '2px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        borderBottom: '2px solid',
                        borderColor: isDefectsActive() ? 'primary.main' : 'action.hover'
                      }
                    }}
                  >
                    Defects
                  </Button>
                  <Menu
                    anchorEl={defectsMenuAnchor}
                    open={Boolean(defectsMenuAnchor)}
                    onClose={handleDefectsMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    {getFilteredMenuItems(defectsMenuItems).map((item) => (
                      <MenuItem
                        key={item.id}
                        component={Link}
                        to={item.path}
                        onClick={handleDefectsMenuClose}
                        selected={isActivePath(item.path)}
                      >
                        <ListItemIcon>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.label} />
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
              
              {/* Reports Dropdown - only show if user has access to any reports */}
              {getFilteredMenuItems(reportsMenuItems).length > 0 && (
                <>
                  <Button
                    onClick={handleReportsMenuOpen}
                    startIcon={<AssessmentIcon />}
                    endIcon={<KeyboardArrowDownIcon />}
                    color={isReportsActive() ? 'primary' : 'inherit'}
                    sx={{
                      mx: 0.5,
                      py: 1,
                      fontWeight: isReportsActive() ? 600 : 400,
                      borderBottom: isReportsActive() ? '2px solid' : '2px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        borderBottom: '2px solid',
                        borderColor: isReportsActive() ? 'primary.main' : 'action.hover'
                      }
                    }}
                  >
                    Reports
                  </Button>
                  <Menu
                    anchorEl={reportsMenuAnchor}
                    open={Boolean(reportsMenuAnchor)}
                    onClose={handleReportsMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    {getFilteredMenuItems(reportsMenuItems).map((item) => (
                      <MenuItem
                        key={item.id}
                        component={Link}
                        to={item.path}
                        onClick={handleReportsMenuClose}
                        selected={isActivePath(item.path)}
                      >
                        <ListItemIcon>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.label} />
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            <Button 
              onClick={handleUserMenuOpen}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{ 
                textTransform: 'none', 
                display: 'flex', 
                alignItems: 'center' 
              }}
            >
              <Avatar sx={{ 
                width: 32, 
                height: 32, 
                backgroundColor: 'primary.main', 
                mr: 1,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.5px'
              }}>
                {getUserInitials()}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" component="div" sx={{ fontWeight: 500, textAlign: 'left' }}>
                    {getUserDisplayName()}
                  </Typography>
                </Box>
                {user?.organizationName && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {user.organizationName}
                  </Typography>
                )}
                {!user?.organizationName && user && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    Loading organization...
                  </Typography>
                )}
              </Box>
            </Button>
            
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                elevation: 2,
                sx: {
                  minWidth: 200,
                  mt: 1,
                }
              }}
            >
              {/* User Info Header */}
              <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {getUserDisplayName()}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {user?.permissionScope === 'organization' ? 'Organization Level' : 'Network/Section Level'}
                  </Typography>
                </Box>
                {user?.organizationName && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    {user.organizationName}
                  </Typography>
                )}
              </Box>

              <MenuItem 
                component={Link} 
                to="/profile" 
                onClick={handleUserMenuClose}
              >
                <ListItemIcon>
                  <AccountCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="My Profile" />
              </MenuItem>

              <MenuItem
                onClick={() => { handleUserMenuClose(); setManualDialogOpen(true); }}
              >
                <ListItemIcon>
                  <MenuBookIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Manual" />
              </MenuItem>

              {/* Manager-specific items */}
              {user?.role === 'manager' && (
                <MenuItem 
                  component={Link} 
                  to="/contractors" 
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <BusinessIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Contractors" />
                </MenuItem>
              )}
              
              {/* Manager-specific Map Layers */}
              {user?.role === 'manager' && (
                <MenuItem 
                  component={Link} 
                  to="/map-layers" 
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <LayersIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Map Layers" />
                </MenuItem>
              )}
              
              {/* Manager-specific menu items */}
              {user?.role === 'manager' && (
                <MenuItem
                  component={Link}
                  to="/users"
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <Badge badgeContent={pendingRequestCount} color="warning" max={99}>
                      <PeopleIcon fontSize="small" />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText primary="Manage Users" />
                </MenuItem>
              )}

              {user?.role === 'manager' && (
                <MenuItem
                  component={Link}
                  to="/email-flow"
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <EmailIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Daily Email Flow" />
                </MenuItem>
              )}

              {user?.role === 'manager' && (
                <MenuItem
                  component={Link}
                  to="/inspection-flow"
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <AccountTreeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Inspection Flow" />
                </MenuItem>
              )}

              {user?.role === 'admin' && user?.organizationId === null && (
                <MenuItem 
                  component={Link} 
                  to="/organizations" 
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <BusinessIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Organizations" />
                </MenuItem>
              )}
              
              {/* Manager-only items - hidden from user and viewer roles */}
              {user?.role === 'manager' && (
                <MenuItem 
                  component={Link} 
                  to="/status" 
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <SignalCellularAltIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="API Status" />
                </MenuItem>
              )}
              
              {user?.role === 'manager' && (
                <MenuItem
                  component={Link}
                  to="/ai-job-status"
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <BiotechIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="AI Job Status" />
                </MenuItem>
              )}

              {user?.role === 'manager' && (
                <MenuItem
                  component={Link}
                  to="/settings"
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Settings" />
                </MenuItem>
              )}
              
              {user?.role === 'manager' && (
                <MenuItem 
                  component={Link} 
                  to="/devices" 
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <DevicesIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Device" />
                </MenuItem>
              )}
              
              <Divider />
              
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>

      <ManualListDialog
        open={manualDialogOpen}
        onClose={() => setManualDialogOpen(false)}
      />
    </>
  );
};

export default Navbar; 